import { Button } from '@creditornot/cb-button';
import { MagnifyingGlass, ShareAndroid } from '@creditornot/cb-icons';
import { ModalFooter } from '@creditornot/cbt-modal';
import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList } from 'react-window';

import { FilterMenu } from 'modules/shared-menu/components/FilterMenu';
import { buildVenueFilterMenuItems } from 'modules/shared-menu/components/FilterMenu/utils';
import { SelectableAccordion, useSelectableAccordion } from 'modules/shared-menu/components/SelectableAccordion';
import { useScopeContext } from 'modules/shared-menu/contexts/ScopeContext';
import { useVenueList } from 'modules/shared-menu/hooks/useVenueList';
import { VenueWithSharedMenuLinkInfo } from 'modules/shared-menu/hooks/useVenuesWithSelectionLinks';
import { ShareMenuWithSelectedVenuesModal } from 'modules/shared-menu/pages/VenueListPage/components/ShareMenuWithSelectedVenuesModal';

import { SelectedVenuesV2 } from '../SelectedVenuesV2';
import { VenueListFooter } from '../VenueListFooter';
import VenueListRowRenderer, { VenueRowData } from '../VenueListRowRenderer';
import {
  SidepanelContent,
  SidepanelContentComponent,
  SidepanelEmptyMessage,
  SidepanelHeader,
  SidepanelRoot,
  SidepanelSearchInput,
  SidepanelToolbar,
  SidepanelToolbarLeft,
  SidepanelToolbarRight,
  SidepanelVenueList,
} from '../sidepanelStyles';

type UnassignedVenuesSidepanelProps = {
  venues: VenueWithSharedMenuLinkInfo[];
  onClose: () => void;
};

const ROW_HEIGHT = 56;

export const UnassignedVenuesSidepanel = ({ venues, onClose }: UnassignedVenuesSidepanelProps) => {
  const { formatMessage } = useIntl();
  const scope = useScopeContext();

  const [isSharingModalOpen, setIsSharingModalOpen] = useState(false);

  const {
    shownVenues,
    selectedVenues,
    selectedVenueIds,
    onRowClick,
    searchInput,
    searchVenues,
    selectAllShownVenues,
    selectedShownVenues,
    deselectAllShownVenues,
    uniqueCities,
    setAppliedFilters,
  } = useVenueList({
    venues,
  });

  const venueFilterMenuItems = buildVenueFilterMenuItems(uniqueCities);

  const {
    handleClearAll: clearAllFilters,
    handleGroupClick: toggleFilterGroup,
    handleItemClick: selectFilter,
    expandedGroups: expandedFilterGroups,
    expandAllGroups: expandAllFilterGroups,
    selectedIds: selectedFilterIds,
  } = useSelectableAccordion(venueFilterMenuItems, setAppliedFilters);

  const areAllVenuesSelected = selectedVenueIds.size === venues?.length;

  return (
    <SidepanelRoot
      ContentComponent={SidepanelContentComponent}
      disableContentPadding
      onDismiss={onClose}
      open
      sizePreset="sideEndSheet:extraLarge"
    >
      <SidepanelHeader closeButtonAriaLabel="Close" onCloseClick={onClose}>
        <FormattedMessage
          id={
            scope.type === 'brand'
              ? 'listing-manager.unassigned-venues.sidepanel.title.brand'
              : 'listing-manager.unassigned-venues.sidepanel.title.merchant'
          }
        />
      </SidepanelHeader>
      <SidepanelContent disablePadding>
        <SidepanelToolbar>
          <SidepanelToolbarLeft>
            <SidepanelSearchInput
              value={searchInput}
              icon={<MagnifyingGlass />}
              placeholder={formatMessage({ id: 'shared-menu.toolbar.search-placeholder' })}
              onChange={e => searchVenues(e.target.value)}
            />
            {venues.length > 0 && (
              <FilterMenu appliedFiltersCount={selectedFilterIds.length}>
                <SelectableAccordion
                  headerLabel={formatMessage({ id: 'shared-menu.toolbar.filter-venues.title' })}
                  items={venueFilterMenuItems}
                  expandedGroups={expandedFilterGroups}
                  onItemClick={selectFilter}
                  onGroupClick={toggleFilterGroup}
                  onClearAllClick={clearAllFilters}
                  expandAllGroups={expandAllFilterGroups}
                  selectedIds={selectedFilterIds}
                  expandedByDefault
                />
              </FilterMenu>
            )}
          </SidepanelToolbarLeft>
          <SidepanelToolbarRight>
            {selectedVenues.length > 0 && (
              <Button
                variant="secondary"
                size="small"
                onClick={() => setIsSharingModalOpen(true)}
                icon={<ShareAndroid />}
              >
                <FormattedMessage id="shared-menu.link.venues-selected.label" />
              </Button>
            )}
          </SidepanelToolbarRight>
        </SidepanelToolbar>
        {selectedVenues.length > 0 && <SelectedVenuesV2 venues={selectedVenues} onDeselectVenue={onRowClick} />}
        <SidepanelVenueList>
          {(!shownVenues || shownVenues.length === 0) && (
            <SidepanelEmptyMessage title={formatMessage({ id: 'menu.menu-sync.empty-venue-list' })} />
          )}
          {shownVenues && shownVenues.length > 0 && (
            <AutoSizer disableWidth>
              {({ height }) => (
                <FixedSizeList<VenueRowData>
                  height={height}
                  width="100%"
                  itemData={{
                    onRowClick,
                    selectedVenueIds,
                    venues: shownVenues,
                  }}
                  itemCount={shownVenues.length}
                  itemSize={ROW_HEIGHT}
                  itemKey={index => shownVenues[index].id}
                  overscanCount={6}
                >
                  {VenueListRowRenderer}
                </FixedSizeList>
              )}
            </AutoSizer>
          )}
        </SidepanelVenueList>
      </SidepanelContent>
      <ModalFooter>
        {shownVenues && (
          <VenueListFooter
            onDeselectAll={deselectAllShownVenues}
            onSelectAll={selectAllShownVenues}
            shownVenues={shownVenues}
            selectedShownVenues={selectedShownVenues}
          />
        )}
      </ModalFooter>

      {isSharingModalOpen && (
        <ShareMenuWithSelectedVenuesModal
          isOpen
          onClose={() => setIsSharingModalOpen(false)}
          selectedVenues={selectedVenues}
          search={searchInput}
          allVenuesSelected={areAllVenuesSelected}
        />
      )}
    </SidepanelRoot>
  );
};
